/**
 * iter632 — Unified Receipt Settings page (Foodics-style).
 * Single source of truth for everything that prints on a thermal receipt:
 * logo, languages, header/footer, invoice title, and 9 toggle options.
 */
import { useState, useEffect } from 'react';
import { DashboardLayout } from '../components/DashboardLayout';
import { SettingsPageHeader } from '@/components/settings/SettingsPageHeader';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Switch } from '../components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Receipt, Image as ImageIcon, RefreshCw, Save } from 'lucide-react';
import { SSoTCompanyInfoBanner } from '../components/common/SSoTCompanyInfoBanner';
import api from '@/lib/api';
import { toast } from 'sonner';

const API_BASE = process.env.REACT_APP_BACKEND_URL;
const LANGS = [
  { id: 'en', label: 'English' },
  { id: 'ar', label: 'العربية' },
  { id: 'fr', label: 'Français' },
  { id: 'es', label: 'Español' },
];

const OPTION_LABELS = [
  // iter684 — Visual layout toggles (top → bottom on receipt)
  ['show_arabic_header', 'Show custom header text (Arabic / multilingual)'],
  ['show_order_box', 'Show big boxed Order# at top'],
  ['show_order_type', 'Show order type label (Pick Up / Dine-in)'],
  ['show_printed_at', 'Show Printed At + Check #'],
  ['show_column_headers', 'Show item columns (Qty | Item | Price)'],
  ['show_order_number', 'Show Order Number (compact line)'],
  ['show_check_number', 'Show Check Number'],
  ['hide_free_modifiers', 'Hide Free Modifier Options'],
  ['show_subtotal', 'Show Subtotal'],
  ['show_vat', 'Show VAT line'],
  ['show_payment_method', 'Show payment method line'],
  ['show_products_count', 'Show "Products Count N" line'],
  ['show_zatca_qr', 'Show ZATCA QR code (KSA tax compliance)'],
  ['show_company_at_bottom', 'Show company name at bottom'],
  ['show_creator_username', 'Show Creator Username'],
];

export default function ReceiptSettingsPage() {
  const [b, setB] = useState({
    logo_url: '', thermal_logo_url: '',
    invoice_title: 'Simplified Tax Invoice',
    footer_text: '', company_name: '',
    receipt_options: {},
  });
  const [printLang, setPrintLang] = useState('main');
  const [mainLang, setMainLang] = useState('en');
  const [localizedLang, setLocalizedLang] = useState('ar');
  const [headerTxt, setHeaderTxt] = useState('');
  const [footerTxt, setFooterTxt] = useState('Thank you!');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => { load(); }, []);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get('/pdf-exports/branding');
      setB({ ...data, receipt_options: data.receipt_options || {} });
      setHeaderTxt(data.receipt_header || '');
      setFooterTxt(data.footer_text || 'Thank you!');
      // iter730 — Language selections finally persist. Defaults if
      // the tenant has never saved them are the same as the React
      // useState defaults so existing users see no change until they
      // touch the dropdowns.
      if (data.print_language) setPrintLang(data.print_language);
      if (data.main_language) setMainLang(data.main_language);
      if (data.localized_language) setLocalizedLang(data.localized_language);
    } catch (e) { toast.error('Failed to load settings'); }
    finally { setLoading(false); }
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.post('/pdf-exports/branding', {
        ...b,
        footer_text: footerTxt,
        invoice_title: b.invoice_title || 'Simplified Tax Invoice',
        // iter730 — Persist language selections.
        print_language: printLang,
        main_language: mainLang,
        localized_language: localizedLang,
      });
      toast.success('Receipt settings saved');
    } catch { toast.error('Failed to save'); }
    finally { setSaving(false); }
  };

  const setOpt = (key, val) => {
    setB(p => ({ ...p, receipt_options: { ...(p.receipt_options || {}), [key]: val } }));
  };

  return (
    <DashboardLayout>
      <div className="max-w-3xl mx-auto px-4 py-6 space-y-5" data-testid="receipt-settings-page">
        <SettingsPageHeader
          icon={Receipt}
          title="Receipt Settings"
          subtitle="Everything that prints on your thermal receipts — in one place."
        />

        <Card data-testid="receipt-form">
          <CardHeader>
            <CardTitle>Receipt Layout</CardTitle>
            <CardDescription>Logo, languages, header, footer and column toggles.</CardDescription>
          </CardHeader>
          <CardContent className="space-y-5">
            {/* Logo lives on the Branding page only (single source of
                truth). Receipts resolve: Branch Logo → Global Logo. */}
            <div className="rounded-lg border border-stone-200 bg-stone-50 p-3 text-xs text-stone-700" data-testid="receipt-logo-note">
              Receipt logos are managed on the <a href="/branding" className="font-semibold text-primary hover:underline">Branding page</a>.
              Each receipt automatically uses the <strong>Branch Logo</strong> when set, otherwise the <strong>Global Logo</strong>.
              Images smaller than 10×10px are skipped automatically.
            </div>

            {/* Languages */}
            <div className="grid sm:grid-cols-3 gap-3">
              <div>
                <Label className="text-xs">Print Language</Label>
                <Select value={printLang} onValueChange={setPrintLang}>
                  <SelectTrigger data-testid="print-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="main">Main</SelectItem>
                    <SelectItem value="localized">Localized</SelectItem>
                    <SelectItem value="both">Both</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Main Language</Label>
                <Select value={mainLang} onValueChange={setMainLang}>
                  <SelectTrigger data-testid="main-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">Localized Language</Label>
                <Select value={localizedLang} onValueChange={setLocalizedLang}>
                  <SelectTrigger data-testid="localized-lang-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{LANGS.map(l => <SelectItem key={l.id} value={l.id}>{l.label}</SelectItem>)}</SelectContent>
                </Select>
              </div>
            </div>

            {/* Header / Footer */}
            <div>
              <Label className="text-xs">Receipt Header</Label>
              <Textarea value={headerTxt} onChange={(e) => setHeaderTxt(e.target.value)} maxLength={200} rows={3} placeholder="e.g., Welcome to Cafe XYZ" data-testid="receipt-header-area" />
              <div className="text-[10px] text-stone-500 text-right">{headerTxt.length}/200</div>
            </div>
            <div>
              <Label className="text-xs">Receipt Footer</Label>
              <Textarea value={footerTxt} onChange={(e) => setFooterTxt(e.target.value)} maxLength={200} rows={3} placeholder="e.g., Thank you — @cafexyz" data-testid="receipt-footer-area" />
              <div className="text-[10px] text-stone-500 text-right">{footerTxt.length}/200</div>
            </div>

            {/* Invoice title */}
            <div>
              <Label className="text-xs">Invoice Title</Label>
              <Input value={b.invoice_title || ''} onChange={(e) => setB(p => ({ ...p, invoice_title: e.target.value }))} placeholder="Simplified Tax Invoice" data-testid="invoice-title-input" />
            </div>

            {/* iter684/iter774 — VAT now sourced from Company Profile (SSoT). Only ZATCA seller name stays here (receipt-specific). */}
            <div className="pt-2 border-t border-stone-200 space-y-3">
              <SSoTCompanyInfoBanner
                editHref="/settings/company-profile"
                scopeLabel="VAT & company info"
                previewFields={['company_name', 'company_vat']}
                data-testid="receipt-company-ssot"
              />
              <div>
                <Label className="text-xs">ZATCA Seller Name</Label>
                <Input
                  value={b.zatca_seller_name || ''}
                  onChange={(e) => setB(p => ({ ...p, zatca_seller_name: e.target.value }))}
                  placeholder="Leave blank to use Company name"
                  data-testid="zatca-seller-input"
                />
                <p className="text-[10px] text-muted-foreground mt-1">Saudi tax authority requires Arabic legal name in the QR (when applicable). VAT number comes from your Company Profile.</p>
              </div>
            </div>

            <div>
              <Label className="text-xs">Company-wide header text (Arabic + English on one block)</Label>
              <Textarea
                value={b.receipt_header_text || ''}
                onChange={(e) => setB(p => ({ ...p, receipt_header_text: e.target.value }))}
                maxLength={400}
                rows={3}
                placeholder="MTR - شركة معيار الذكي&#10;فاتورة ضريبية مبسطة"
                data-testid="receipt-header-text-input"
              />
              <p className="text-[10px] text-muted-foreground">
                Shown above the order box on every branch&apos;s receipt. Each branch can also set its own header which will replace this one.
              </p>
            </div>

            {/* iter684 — Logo upload guidelines */}
            <div className="pt-2 border-t border-stone-200 bg-amber-50/50 -mx-6 px-6 py-3">
              <Label className="text-xs font-semibold flex items-center gap-1">
                <ImageIcon size={12} /> Logo guidelines (for thermal printer)
              </Label>
              <ul className="text-[11px] text-stone-700 leading-relaxed mt-1 space-y-0.5">
                <li>• <strong>Format</strong>: PNG or JPG. PNG with transparent background is best.</li>
                <li>• <strong>Width</strong>: at most 384 px (for 80 mm paper) or 256 px (58 mm). Larger images are auto-downscaled.</li>
                <li>• <strong>Height</strong>: at most 200 px. Taller logos waste paper.</li>
                <li>• <strong>Style</strong>: line art / simple shapes work best. Photographic logos get noisy when dithered to black-and-white.</li>
                <li>• <strong>Hosting</strong>: must be on the same domain (e.g. <code>smartoerp.com</code>) OR have <code>Access-Control-Allow-Origin: *</code>. iPad Safari blocks cross-origin canvas reads.</li>
              </ul>
            </div>

            {/* toggles */}
            <div className="space-y-2 pt-3 border-t border-stone-200">
              <Label className="text-sm font-semibold">Show / hide on receipt</Label>
              <div className="grid sm:grid-cols-2 gap-y-2 gap-x-6">
                {OPTION_LABELS.map(([key, label]) => (
                  <div key={key} className="flex items-center justify-between gap-3 py-1">
                    <Label htmlFor={`opt-${key}`} className="text-xs cursor-pointer flex-1">{label}</Label>
                    <Switch
                      id={`opt-${key}`}
                      checked={!!b.receipt_options?.[key]}
                      onCheckedChange={(v) => setOpt(key, v)}
                      data-testid={`opt-${key}`}
                    />
                  </div>
                ))}
              </div>
            </div>
          </CardContent>
        </Card>

        <div className="flex justify-end">
          <Button onClick={save} disabled={saving || loading} className="bg-indigo-600 hover:bg-indigo-700 text-white" data-testid="receipt-save-btn">
            {saving ? <RefreshCw size={14} className="animate-spin mr-1" /> : <Save size={14} className="mr-1" />}
            Save Changes
          </Button>
        </div>
      </div>
    </DashboardLayout>
  );
}
